/**
 * GameRegistry — Plan 08-03 Task 2.
 *
 * resolveGame(kind, rawConfig) → Game instance.
 *
 * kind 에 따라 해당 Zod schema 로 config 를 parse 후 game 생성. config 가 invalid
 * (확률 합 ≠ 1.0, 현금 환원 키, reels ≠ 3 등) 면 ZodError.
 *
 * 본 함수는 @loop/contracts schema 를 dependency 로 한다 (08-03 Task 1 확장).
 */
import type { Game, GameKind } from './game-interface.js';
import {
  RouletteConfigSchema,
  SlotConfigSchema,
  RaceConfigSchema,
} from '@loop/contracts/rest';
import { RouletteGame } from './roulette/roulette.game.js';
import { SlotGame } from './slot/slot.game.js';
import { RaceGame } from './race/race.game.js';

export interface GameRegistry {
  resolveGame(kind: GameKind, rawConfig: unknown): Game;
}

/**
 * kind 에 따라 schema parse 후 Game 인스턴스 반환.
 * @throws ZodError — invalid config.
 * @throws Error    — 허용 외 kind.
 */
export function resolveGame(kind: GameKind, rawConfig: unknown): Game {
  switch (kind) {
    case 'roulette': {
      const cfg = RouletteConfigSchema.parse(rawConfig);
      return new RouletteGame(cfg);
    }
    case 'slot': {
      const cfg = SlotConfigSchema.parse(rawConfig);
      return new SlotGame(cfg);
    }
    case 'race': {
      const cfg = RaceConfigSchema.parse(rawConfig);
      return new RaceGame(cfg);
    }
    default: {
      throw new Error(`Unknown game kind: ${String(kind)}`);
    }
  }
}

export const gameRegistry: GameRegistry = {
  resolveGame,
};
